import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';
import {getOnlyElement} from 'external/dev_april_corgi+/js/common/collections';

import {Graph, Pad, PipeId, Node, NodeSpec} from '../execution/graph';
import {
  PTransformer,
  PipeContext,
  Source,
  walkMaterials,
} from '../execution/pipes';
import {ArrayValue, PlatedMaterialValue} from '../types/data';

export const NODE = {
  id: 'node_breakout_materials',
  label: 'Breakout materials',
  inputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
  ],
  outputs: [],
  pipe: ({graph, node, overlay}) => {
    const sources = overlay.padToSources(
      checkExists(node.inputs.get('materials')),
    );
    const adjuster = new PadAdjustingSink(
      overlay.nextIdFor(node),
      graph,
      node,
      sources as Array<
        Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
      >,
    );

    for (const pad of node.outputs.values()) {
      overlay.setPadSource(
        pad,
        new PlatedMaterialFilteringTransformer(
          overlay.nextIdFor(node),
          pad.label,
          adjuster,
        ),
      );
    }
  },
} as const satisfies NodeSpec;

class PadAdjustingSink extends PTransformer<
  ArrayValue<PlatedMaterialValue> | PlatedMaterialValue,
  ArrayValue<PlatedMaterialValue>
> {
  constructor(
    id: PipeId,
    private readonly graph: Graph,
    private readonly node: Node,
    sources: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
  ) {
    super(id, sources);
  }

  async collate(context: PipeContext): Promise<void> {
    const elements: PlatedMaterialValue[] = [];
    const idsByLabel = new Map();
    const need = new Set<string>();
    for (const {value, reject} of this.values.values()) {
      walkMaterials(value, reject, v => {
        elements.push(v);
        const existing = idsByLabel.get(v.material.label);
        if (existing) {
          reject(`Material ${v.material.label} is provided twice`);
          return;
        }
        idsByLabel.set(v.material.label, v.material.id);
        need.add(v.material.label);
      });
    }

    const constraints = await this.gatherConstraints(
      {kind: 'array', elements},
      context,
    );
    const promises = [];
    for (const {value, resolve, reject} of this.values.values()) {
      promises.push(
        resolve(
          walkMaterials(value, reject, () => {
            return (constraints.array ?? []).shift();
          }),
        ),
      );
    }
    await Promise.all(promises);

    for (const pad of [...this.node.outputs.values()]) {
      if (need.has(pad.label)) {
        need.delete(pad.label);
      } else {
        await context.changes.removePad(pad);
      }
    }

    for (const label of need) {
      const pad: Pad<'output'> = {
        id: `dynamic-${this.graph.nextId()}`,
        node: this.node,
        direction: 'output',
        label,
        type: {
          kind: 'plated_material',
          material: undefined,
        },
        links: new Map(),
      };
      context.changes.addPad(pad);
    }
  }
}

class PlatedMaterialFilteringTransformer extends PTransformer<
  ArrayValue<PlatedMaterialValue>,
  PlatedMaterialValue
> {
  constructor(
    id: PipeId,
    private readonly label: string,
    source: Source<ArrayValue<PlatedMaterialValue>>,
  ) {
    super(id, [source]);
  }

  async collate(context: PipeContext): Promise<void> {
    const materials: PlatedMaterialValue[] = [];
    for (const {value, reject} of this.values.values()) {
      walkMaterials(value, reject, v => {
        if (v.material.label === this.label) {
          materials.push(v);
        }
      });
    }

    if (materials.length === 0) {
      const constraints = await this.gatherConstraints(undefined, context);
      for (const {resolve} of this.values.values()) {
        resolve(constraints);
      }
      return;
    }

    const value = getOnlyElement(materials);
    const constraints = await this.gatherConstraints(value, context);
    for (const {value, resolve, reject} of this.values.values()) {
      resolve(
        walkMaterials(value, reject, v => {
          if (v.material.label === this.label) {
            return constraints;
          } else {
            return {};
          }
        }),
      );
    }
  }
}
